import React, { useState } from 'react';
import {
    IconTextButton,
    ITreeSelectDataSource,
    notification,
    AdvanceTable,
    IBasicTableProps,
    IAdvanceTableToolbarProps,
    Spinner,
} from 'ui-lib';
import { FILE_TRANSFER_STATUS, FileTransferDto } from '../../../../dto/file-transfer.dto';
import fileTransferCss from './new-file-transfer.css';
import { useFileTransfersStore } from 'store';
import { downloadFileFromUrl, getErrorMessage } from 'common-utils';
import { TableUtils } from 'pages/private/components/table-utils';
import { confirmAction } from 'pages/private/admin/utils';
import { EditTransferFile } from './edit-transfer-file';
import { SendFiles } from './send-files';
import { DeviceGroupSelect } from 'pages/private/components/device-groups-select';
import { UploadFileType } from 'dto/upload-file.dto';
import { useUrlSearch } from 'hooks/use-search.hook';

interface ITransferFilesListingProps {
    selectedClientAccountId: number;
    deviceGroupsDataSource: ITreeSelectDataSource[];
    deviceGroupLoading: boolean;
    lockOrganisationSelect: (boolean) => void;
}

export enum TRANSFER_FILE_STATE {
    LIST = 'List',
    EDIT = 'Edit',
    SEND = 'Send'
}

export const PERMISSIONS_OPTIONS = [
    { value: 1, label: 'Execute' },
    { value: 2, label: 'Write' },
    { value: 4, label: 'Read' },
];

export enum TRANSFER_OPTION_VALUE {
    USE_OUT_OF_SERVICE_WINDOWS = 1,
    REQUEST_REBOOT = 2,
}

export const TRANSFER_OPTIONS = [
    { value: TRANSFER_OPTION_VALUE.USE_OUT_OF_SERVICE_WINDOWS, label: 'Use Out-Of-Service Windows' },
    { value: TRANSFER_OPTION_VALUE.REQUEST_REBOOT, label: 'Request Reboot' },
];

export const basicColumns = [
    ['File Name', 'fileName'],
    ['Version', 'version'],
    ['Description', 'description'],
].map(e =>
    TableUtils.createBasicFilterColumn({ title: e[0], key: e[1] })
);

export const enumColumns = [
    { title: 'Type', key: 'type', enumObject: UploadFileType },
    { title: 'Status', key: 'status', enumObject: FILE_TRANSFER_STATUS },
].map(e =>
    TableUtils.createBasicEnumFilterColumn({
        title: e.title,
        key: e.key,
        filters: Object.keys(e.enumObject).map(v => ({ text: e.enumObject[v], value: v }))
    })
);

export const timeColumns = [
    TableUtils.createBasicFilterTimeColumn({ key: 'updatedAt', title: 'Updated At' }),
];

export const TransferFilesTab = (props: ITransferFilesListingProps) => {
    const [transferFilesSource, setTransferFilesSource] = React.useState<FileTransferDto[]>([]);
    const [state, setState] = useState<TRANSFER_FILE_STATE>(TRANSFER_FILE_STATE.LIST);
    const fileTransfersStore = useFileTransfersStore();
    const { getSearchParam, updateSearchParams } = useUrlSearch();
    const [editFileTransfer, setEditFileTransfer] = useState<FileTransferDto>(null);
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [reloading, setReloading] = React.useState<boolean>(false);
    const [selectedFileTransfers, setSelectedFileTransfers] = React.useState<FileTransferDto[]>([]);

    const OnDeleteButtonClick = async (fileTransfer: FileTransferDto) => {
        confirmAction(
            undefined,
            'Delete Transfer File',
            `Are you sure you want to delete the transfer file ${fileTransfer.fileName}?`,
            async () => {
                try {
                    await fileTransfersStore.deleteFileTransfer(props.selectedClientAccountId, fileTransfer.uuid);
                    setTransferFilesSource((prev) => prev.filter((item) => item.uuid !== fileTransfer.uuid));
                    notification.success({
                        message: 'Transfer File Successfully Deleted',
                        description: `Transfer file ${fileTransfer.fileName} has been deleted.`,
                    });

                } catch (error) {
                    notification.error({
                        message: 'Delete transfer file error',
                        description: getErrorMessage(error),
                    });
                }
            });
    }

    const ActionComponent = (props: { clientAccountId: number, record: FileTransferDto }): JSX.Element => {
        const fileTransfer: FileTransferDto = props.record;
        const editable = fileTransfer.status !== FILE_TRANSFER_STATUS.TRANSFERRING && fileTransfer.type !== UploadFileType.SCR;
        const downloadable = fileTransfer.type !== UploadFileType.SCR;
        return (
            <div className={fileTransferCss.actionButtons}>
                <IconTextButton
                    className={fileTransferCss.linkButton}
                    key='fileTransferEdit'
                    type='link'
                    label='Edit'
                    disabled={!editable}
                    onClick={() => {
                        if (!selectedGroup) {
                            notification.error({
                                message: 'Error',
                                description: 'Please select a device group before updating files.',
                            });
                            return;
                        }
                        setEditFileTransfer(fileTransfer);
                        setState(TRANSFER_FILE_STATE.EDIT);
                    }}
                />
                <IconTextButton
                    className={fileTransferCss.linkButton}
                    key='fileTransferDownload'
                    type='link'
                    label='Download'
                    disabled={!downloadable}
                    onClick={() => {
                        downloadFileFromUrl(fileTransfer.awsArn, fileTransfer.fileName);
                    }}
                />
                <IconTextButton
                    className={fileTransferCss.linkButton}
                    key='fileTransferDelete'
                    type='link'
                    label='Delete'
                    onClick={() => OnDeleteButtonClick(fileTransfer)}
                    disabled={!editable}
                />
            </div>
        );
    };

    const actionsColumn = TableUtils.createExtraColumn({
        key: 'actions',
        title: 'Actions',
        render: (text: string, record: FileTransferDto) => <ActionComponent clientAccountId={props.selectedClientAccountId} record={record} />,
    });

    const uploadFilesTable: IBasicTableProps = {
        dataSource: transferFilesSource,
        columns: [...basicColumns, ...enumColumns, ...timeColumns, actionsColumn],
        rowKey: 'uuid',
        rowSelection: {
            type: 'checkbox',
            getCheckboxProps: (record: FileTransferDto): { value: FileTransferDto; disabled: boolean } => {
                return {
                    value: record,
                    disabled: record.status === FILE_TRANSFER_STATUS.TRANSFERRING,
                };
            },
            onChange: (selectedRowKeys, selectedRows): void => {
                setSelectedFileTransfers(selectedRows as FileTransferDto[]);
            }
        },
    };

    const resetData = () => {
        setTransferFilesSource([]);
        setSelectedFileTransfers([]);
        setEditFileTransfer(undefined);
    }

    const onReloadData = async () => {
        setReloading(true);
        try {
            resetData();
            if (!props.selectedClientAccountId || !selectedGroup) {
                setReloading(false);
                return;
            }
            const fileTransfers = await fileTransfersStore.getFileTransfers(props.selectedClientAccountId, selectedGroup)
            setTransferFilesSource(fileTransfers);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: `Failed to load file transfers: ${getErrorMessage(error)}`,
            });
        }
        setReloading(false);
    };

    const onSend = () => {
        if (!selectedFileTransfers || selectedFileTransfers.length === 0) {
            notification.error({
                message: 'Error',
                description: 'Please select at least one file transfer to send.',
            });
            return;
        }
        setState(TRANSFER_FILE_STATE.SEND);
    };

    const onCancel = async () => {
        setState(TRANSFER_FILE_STATE.LIST);
        await onReloadData();
    }

    React.useEffect(() => {
        setSelectedGroup(() => {
            const paramDeviceGroup = getSearchParam('deviceGroup') || '';
            return props.deviceGroupsDataSource?.find(dg => dg.treeDataNode.value === paramDeviceGroup) ? paramDeviceGroup : '';
        });
    }, [props.deviceGroupsDataSource]);

    React.useEffect(() => {
        (async () => {
            await onReloadData();
        })();
    }, [selectedGroup]);

    React.useEffect(() => {
        if (state === TRANSFER_FILE_STATE.LIST) {
            props.lockOrganisationSelect(false);
        }
        else {
            props.lockOrganisationSelect(true);
        }
    }, [state]);

    const sendToolbarButtons = <IconTextButton
        key='sendItem'
        label='Send'
        iconName='paper-plane'
        type='primary'
        onClick={onSend}
        className={fileTransferCss.extraSpacing}
    />;

    const uploadFilesTableToolbar: IAdvanceTableToolbarProps = {
        onReload: onReloadData,
        extraItems: [sendToolbarButtons],
    };

    const selectDeviceGroupComponent = (
        <DeviceGroupSelect
            placeholder='Select Device Group'
            treeDataSource={props.deviceGroupsDataSource}
            expandAll={true}
            onChange={(value): void => {
                setSelectedGroup(value || '');
                updateSearchParams({ deviceGroup: value || null });
            }}
            disabled={props.deviceGroupLoading}
            className={fileTransferCss.orgSelect}
            value={selectedGroup}
        />
    );

    return (
        <React.Fragment>
            <Spinner spinning={props.deviceGroupLoading} className={fileTransferCss.spinner}>
                {state === TRANSFER_FILE_STATE.LIST && (
                    <AdvanceTable
                        toolbar={uploadFilesTableToolbar}
                        title={selectDeviceGroupComponent}
                        table={uploadFilesTable}
                        loadingState={reloading}
                    />
                )}
                {state === TRANSFER_FILE_STATE.EDIT && (
                    <EditTransferFile
                        selectedClientAccountId={props.selectedClientAccountId}
                        selectedDeviceGroup={selectedGroup}
                        selectedFileTransfer={editFileTransfer}
                        onCancel={onCancel}
                    />
                )}
                {state === TRANSFER_FILE_STATE.SEND && (
                    <SendFiles
                        selectedClientAccountId={props.selectedClientAccountId}
                        selectedDeviceGroup={selectedGroup}
                        selectedFileTransfers={selectedFileTransfers}
                        onCancel={onCancel}
                    />
                )}
            </Spinner>
        </React.Fragment>
    );
};